import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      const response = await api.post('/auth/forgot-password', { email: email.toLowerCase() });
      setStatus({ type: 'success', text: response.data.message });
    } catch (err) {
      const isMissing = err.response?.status === 404;
      setStatus({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Something went wrong',
        isUserMissing: isMissing
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusColors = {
    success: { color: 'var(--color-success)', bg: 'var(--color-success-bg)', border: 'var(--color-success-border)' },
    error:   { color: 'var(--color-error)',   bg: 'var(--color-error-bg)',   border: 'var(--color-error-border)'   },
  };

  return (
    <div style={styles.page}>
      <div className="auth-blob-decoration" style={styles.blobTopRight} aria-hidden="true" />
      <div className="auth-blob-decoration" style={styles.blobBottomLeft} aria-hidden="true" />

      <main style={styles.card} className="glass-panel auth-card-container">
        <header style={styles.brandArea}>
          <span style={styles.logoMark} aria-hidden="true">⬡</span>
          <span style={styles.brandName}>RezFlow</span>
        </header>

        <div style={styles.iconWrap} aria-hidden="true">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--color-charcoal-600)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>

        <h2 style={styles.heading}>Reset your password</h2>
        <p style={styles.subtext}>
          Enter your email and we'll send you a secure link to create a new password.
        </p>

        {status?.type === 'success' ? (
          <div style={{ ...styles.statusMsg, color: statusColors.success.color, backgroundColor: statusColors.success.bg, borderColor: statusColors.success.border }}>
            <strong>Check your inbox!</strong>
            <br />
            {status.text}
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={styles.form} noValidate>
            <div style={styles.fieldWrap}>
              <label htmlFor="fp-email" style={styles.label}>Email address</label>
              <input
                id="fp-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value.toLowerCase())}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                style={{ ...styles.input, ...(isFocused ? styles.inputFocused : {}) }}
                disabled={isSubmitting}
                autoComplete="email"
                required
              />
            </div>

            {status?.type === 'error' && (
              <div style={{ ...styles.statusMsg, color: statusColors.error.color, backgroundColor: statusColors.error.bg, borderColor: statusColors.error.border }}>
                {status.text}
              </div>
            )}

            <button
              type="submit"
              style={{ ...styles.submitBtn, opacity: isSubmitting ? 0.65 : 1 }}
              disabled={isSubmitting}
              className="hover-lift"
            >
              {isSubmitting ? (
                <span style={styles.btnInner}>
                  <span style={styles.btnSpinner} />
                  Sending…
                </span>
              ) : 'Send Reset Link'}
            </button>

            {status?.type === 'error' && status.isUserMissing && (
              <Link
                to="/signup"
                style={styles.signupBtn}
                className="hover-lift"
              >
                Create your account here
              </Link>
            )}
          </form>
        )}

        <p style={styles.footerLink}>
          <Link to="/login" style={styles.link}>← Back to Sign In</Link>
        </p>
      </main>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '24px 20px',
    background: 'linear-gradient(145deg, #f0ede7 0%, #e8eee5 55%, #e7ecf0 100%)',
    position: 'relative', overflow: 'hidden',
  },
  blobTopRight: {
    position: 'absolute', top: '-120px', right: '-140px',
    width: '400px', height: '400px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(22,163,74,0.07) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  blobBottomLeft: {
    position: 'absolute', bottom: '-100px', left: '-120px',
    width: '360px', height: '360px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(35,43,34,0.06) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  card: {
    position: 'relative', zIndex: 1,
    width: '100%', maxWidth: '420px',
    borderRadius: 'var(--radius-2xl)', padding: '40px 40px 36px',
    animation: 'rz-fadeIn 0.4s ease-out both',
    textAlign: 'center',
  },
  brandArea: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '24px',
  },
  logoMark: { fontSize: '22px', color: 'var(--color-charcoal-800)', lineHeight: 1 },
  brandName: {
    fontFamily: 'var(--font-heading)', fontSize: '22px', fontWeight: 700,
    letterSpacing: '-0.4px', color: 'var(--color-charcoal-900)',
  },
  iconWrap: {
    width: '72px', height: '72px', borderRadius: '20px',
    backgroundColor: 'var(--color-surface-alt)',
    border: '1px solid var(--color-border)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 20px',
    boxShadow: 'var(--shadow-sm)',
  },
  heading: {
    fontFamily: 'var(--font-heading)', fontSize: 'var(--text-2xl)',
    fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: '10px',
  },
  subtext: {
    fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)',
    lineHeight: 'var(--line-height-loose)', marginBottom: '28px',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '16px', textAlign: 'left' },
  fieldWrap: { display: 'flex', flexDirection: 'column', gap: '7px' },
  label: {
    fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
  },
  input: {
    padding: '13px 14px', border: '1.5px solid var(--color-border-strong)',
    borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
    color: 'var(--color-text-primary)', backgroundColor: 'var(--color-surface)',
    outline: 'none', width: '100%',
    transition: 'border-color var(--transition-base), box-shadow var(--transition-base)',
  },
  inputFocused: {
    borderColor: 'var(--color-charcoal-700)',
    boxShadow: 'var(--glow-accent)',
  },
  submitBtn: {
    width: '100%', padding: '14px 20px',
    background: 'linear-gradient(135deg, var(--color-charcoal-800) 0%, var(--color-charcoal-700) 100%)',
    color: 'var(--color-text-on-dark)', border: 'none',
    borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-heading)',
    fontWeight: 'var(--font-weight-bold)', fontSize: 'var(--text-base)',
    boxShadow: '0 4px 14px rgba(13,15,13,0.18)',
    transition: 'opacity var(--transition-base), transform var(--transition-base), box-shadow var(--transition-base)',
  },
  signupBtn: {
    width: '100%', padding: '14px 20px',
    background: 'linear-gradient(135deg, var(--color-emerald-700) 0%, var(--color-emerald-600) 100%)',
    color: 'var(--color-text-on-dark)', border: 'none',
    borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-heading)',
    fontWeight: 'var(--font-weight-bold)', fontSize: 'var(--text-base)',
    boxShadow: '0 4px 14px rgba(16,185,129,0.18)',
    textAlign: 'center',
    textDecoration: 'none',
    display: 'block',
    transition: 'opacity var(--transition-base), transform var(--transition-base), box-shadow var(--transition-base)',
  },
  btnInner: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '10px' },
  btnSpinner: {
    display: 'inline-block', width: '16px', height: '16px', borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff',
    animation: 'rz-spin 0.8s linear infinite',
  },
  statusMsg: {
    padding: '14px 16px', borderRadius: 'var(--radius-md)',
    border: '1px solid', fontSize: 'var(--text-sm)',
    lineHeight: 'var(--line-height-loose)',
    animation: 'rz-fadeInFlat 0.2s ease-out',
    marginBottom: '4px',
  },
  footerLink: {
    textAlign: 'center', marginTop: '24px',
    fontSize: 'var(--text-sm)',
  },
  link: {
    color: 'var(--color-text-secondary)', fontWeight: 'var(--font-weight-medium)',
    transition: 'color var(--transition-fast)',
  },
};

export default ForgotPassword;